type AmbientMode = 'space' | 'planet' | null;

interface PlanetAmbientNodes {
  readonly source: AudioBufferSourceNode;
  readonly gain: GainNode;
  readonly filter: BiquadFilterNode;
  readonly filterLfo: OscillatorNode;
  readonly filterLfoGain: GainNode;
}

interface SpaceAmbientNodes {
  readonly oscA: OscillatorNode;
  readonly oscB: OscillatorNode;
  readonly gain: GainNode;
  readonly lfo: OscillatorNode;
  readonly lfoGain: GainNode;
}

interface EngineNodes {
  readonly noise: AudioBufferSourceNode;
  readonly filter: BiquadFilterNode;
  readonly gain: GainNode;
  readonly toneA: OscillatorNode;
  readonly toneB: OscillatorNode;
  readonly toneFilter: BiquadFilterNode;
  readonly toneGain: GainNode;
}

interface JumpDriveNodes {
  readonly noise: AudioBufferSourceNode;
  readonly noiseFilter: BiquadFilterNode;
  readonly noiseGain: GainNode;
  readonly sub: OscillatorNode;
  readonly subGain: GainNode;
  readonly hum: OscillatorNode;
  readonly humFilter: BiquadFilterNode;
  readonly humGain: GainNode;
  readonly modLfo: OscillatorNode;
  readonly modLfoGain: GainNode;
}

export class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private engineBus: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  private planet: PlanetAmbientNodes | null = null;
  private space: SpaceAmbientNodes | null = null;
  private engine: EngineNodes | null = null;
  private jumpDrive: JumpDriveNodes | null = null;

  private pendingAmbient: AmbientMode = null;
  private pendingEngine = false;
  private engineLevel = 0;

  private masterVolume = 0.55;

  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();

    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.masterVolume;
    this.masterGain.connect(this.ctx.destination);

    this.sfxGain = this.ctx.createGain();
    this.sfxGain.gain.value = 0.9;
    this.sfxGain.connect(this.masterGain);

    this.ambientGain = this.ctx.createGain();
    this.ambientGain.gain.value = 1;
    this.ambientGain.connect(this.masterGain);

    this.engineBus = this.ctx.createGain();
    this.engineBus.gain.value = 1;
    this.engineBus.connect(this.masterGain);

    this.noiseBuffer = this.createNoiseBuffer(3);

    if (this.pendingAmbient === 'space') this.startSpaceAmbient();
    else if (this.pendingAmbient === 'planet') this.startPlanetAmbient();

    if (this.pendingEngine) this.startEngine();
  }

  isReady(): boolean {
    return this.ctx !== null;
  }

  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.linearRampToValueAtTime(
        this.masterVolume,
        this.ctx.currentTime + 0.1
      );
    }
  }

  playFootstep(running: boolean): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer) return;
    const now = this.ctx.currentTime;
    const source = this.ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.playbackRate.value = 0.85 + Math.random() * 0.4;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = running ? 1100 : 780;
    filter.Q.value = 0.8;

    const highpass = this.ctx.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 120;

    const gain = this.ctx.createGain();
    const peak = running ? 0.22 : 0.14;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peak, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);

    source.connect(filter).connect(highpass).connect(gain).connect(this.sfxGain);
    const offset = Math.random() * (this.noiseBuffer.duration - 0.15);
    source.start(now, offset, 0.16);
  }

  playJump(): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer) return;
    const now = this.ctx.currentTime;

    const exhale = this.ctx.createBufferSource();
    exhale.buffer = this.noiseBuffer;
    exhale.playbackRate.value = 1.05;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(620, now);
    filter.frequency.exponentialRampToValueAtTime(950, now + 0.08);
    filter.Q.value = 0.7;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.09, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
    exhale.connect(filter).connect(gain).connect(this.sfxGain);
    exhale.start(now, Math.random() * 1, 0.16);

    const servo = this.ctx.createOscillator();
    servo.type = 'triangle';
    servo.frequency.setValueAtTime(260, now);
    servo.frequency.exponentialRampToValueAtTime(170, now + 0.05);
    const servoGain = this.ctx.createGain();
    servoGain.gain.setValueAtTime(0.0001, now);
    servoGain.gain.exponentialRampToValueAtTime(0.055, now + 0.004);
    servoGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
    servo.connect(servoGain).connect(this.sfxGain);
    servo.start(now);
    servo.stop(now + 0.07);
  }

  playLand(): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer) return;
    const now = this.ctx.currentTime;

    const boot = this.ctx.createBufferSource();
    boot.buffer = this.noiseBuffer;
    boot.playbackRate.value = 0.7;
    const bootFilter = this.ctx.createBiquadFilter();
    bootFilter.type = 'lowpass';
    bootFilter.frequency.value = 440;
    bootFilter.Q.value = 0.9;
    const bootGain = this.ctx.createGain();
    bootGain.gain.setValueAtTime(0.0001, now);
    bootGain.gain.exponentialRampToValueAtTime(0.18, now + 0.005);
    bootGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
    boot.connect(bootFilter).connect(bootGain).connect(this.sfxGain);
    boot.start(now, Math.random() * 1.5, 0.12);

    const pop = this.ctx.createOscillator();
    pop.type = 'sine';
    pop.frequency.setValueAtTime(90, now);
    pop.frequency.exponentialRampToValueAtTime(55, now + 0.06);
    const popGain = this.ctx.createGain();
    popGain.gain.setValueAtTime(0.0001, now);
    popGain.gain.exponentialRampToValueAtTime(0.085, now + 0.008);
    popGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
    pop.connect(popGain).connect(this.sfxGain);
    pop.start(now);
    pop.stop(now + 0.1);

    const rustle = this.ctx.createBufferSource();
    rustle.buffer = this.noiseBuffer;
    rustle.playbackRate.value = 1.3;
    const rustleFilter = this.ctx.createBiquadFilter();
    rustleFilter.type = 'bandpass';
    rustleFilter.frequency.value = 1800;
    rustleFilter.Q.value = 0.8;
    const rustleGain = this.ctx.createGain();
    rustleGain.gain.setValueAtTime(0.0001, now + 0.008);
    rustleGain.gain.exponentialRampToValueAtTime(0.045, now + 0.018);
    rustleGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.09);
    rustle.connect(rustleFilter).connect(rustleGain).connect(this.sfxGain);
    rustle.start(now + 0.005, Math.random() * 1, 0.1);
  }

  playLaser(): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer) return;
    const now = this.ctx.currentTime;

    const sweep = this.ctx.createBufferSource();
    sweep.buffer = this.noiseBuffer;
    sweep.playbackRate.value = 1 + Math.random() * 0.2;
    const sweepFilter = this.ctx.createBiquadFilter();
    sweepFilter.type = 'lowpass';
    sweepFilter.frequency.setValueAtTime(1800, now);
    sweepFilter.frequency.exponentialRampToValueAtTime(380, now + 0.04);
    sweepFilter.Q.value = 0.9;
    const sweepGain = this.ctx.createGain();
    sweepGain.gain.setValueAtTime(0.0001, now);
    sweepGain.gain.exponentialRampToValueAtTime(0.2, now + 0.003);
    sweepGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.055);
    sweep.connect(sweepFilter).connect(sweepGain).connect(this.sfxGain);
    sweep.start(now, Math.random() * 1.3, 0.07);

    const thump = this.ctx.createOscillator();
    thump.type = 'triangle';
    thump.frequency.setValueAtTime(135, now);
    thump.frequency.exponentialRampToValueAtTime(55, now + 0.05);
    const thumpGain = this.ctx.createGain();
    thumpGain.gain.setValueAtTime(0.0001, now);
    thumpGain.gain.exponentialRampToValueAtTime(0.18, now + 0.004);
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.07);
    thump.connect(thumpGain).connect(this.sfxGain);
    thump.start(now);
    thump.stop(now + 0.09);
  }

  /** Soft "drilling" tick — a mining-laser specific cue distinct from
   *  the combat playHit so the player can tell at a glance whether they're
   *  shooting or mining by ear. Layered: a low rumble + a high crackle. */
  playMiningTick(): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer) return;
    const now = this.ctx.currentTime;

    const rumble = this.ctx.createOscillator();
    rumble.type = 'sawtooth';
    rumble.frequency.setValueAtTime(180, now);
    rumble.frequency.exponentialRampToValueAtTime(110, now + 0.18);
    const rumbleFilter = this.ctx.createBiquadFilter();
    rumbleFilter.type = 'lowpass';
    rumbleFilter.frequency.value = 460;
    const rumbleGain = this.ctx.createGain();
    rumbleGain.gain.setValueAtTime(0.0001, now);
    rumbleGain.gain.exponentialRampToValueAtTime(0.09, now + 0.012);
    rumbleGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    rumble.connect(rumbleFilter).connect(rumbleGain).connect(this.sfxGain);
    rumble.start(now);
    rumble.stop(now + 0.25);

    const crackle = this.ctx.createBufferSource();
    crackle.buffer = this.noiseBuffer;
    crackle.playbackRate.value = 1.6 + Math.random() * 0.3;
    const crackleFilter = this.ctx.createBiquadFilter();
    crackleFilter.type = 'highpass';
    crackleFilter.frequency.value = 2200;
    const crackleGain = this.ctx.createGain();
    crackleGain.gain.setValueAtTime(0.0001, now);
    crackleGain.gain.exponentialRampToValueAtTime(0.05, now + 0.008);
    crackleGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    crackle.connect(crackleFilter).connect(crackleGain).connect(this.sfxGain);
    crackle.start(now, Math.random() * 1.2, 0.22);
  }

  playHit(): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer) return;
    const now = this.ctx.currentTime;
    const source = this.ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.playbackRate.value = 1.5;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2400;
    filter.Q.value = 3.5;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.22, now + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);

    source.connect(filter).connect(gain).connect(this.sfxGain);
    source.start(now, Math.random() * 1.2, 0.09);
  }

  /** Heavier "your hull just took a beating" cue distinct from playHit (which
   *  is the lighter shot-impact ping). Combines a sub thump, a metallic clank
   *  on a midband filter, and a shimmer of high-frequency debris noise. The
   *  `intensity` knob (0..1) scales loudness so a glancing scrape sounds
   *  different from a full broadside. */
  playHullDamage(intensity = 0.5): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer) return;
    const now = this.ctx.currentTime;
    const i = Math.max(0.15, Math.min(1, intensity));

    // Sub thump — gut-punch that signals "hull stressed".
    const sub = this.ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(110, now);
    sub.frequency.exponentialRampToValueAtTime(42, now + 0.18);
    const subGain = this.ctx.createGain();
    subGain.gain.setValueAtTime(0.0001, now);
    subGain.gain.exponentialRampToValueAtTime(0.32 * i, now + 0.012);
    subGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
    sub.connect(subGain).connect(this.sfxGain);
    sub.start(now);
    sub.stop(now + 0.36);

    // Metallic clank — sharper midband transient, the "armour took it" edge.
    const clank = this.ctx.createBufferSource();
    clank.buffer = this.noiseBuffer;
    clank.playbackRate.value = 1.2 + Math.random() * 0.15;
    const clankFilter = this.ctx.createBiquadFilter();
    clankFilter.type = 'bandpass';
    clankFilter.frequency.setValueAtTime(820, now);
    clankFilter.frequency.exponentialRampToValueAtTime(380, now + 0.12);
    clankFilter.Q.value = 2.6;
    const clankGain = this.ctx.createGain();
    clankGain.gain.setValueAtTime(0.0001, now);
    clankGain.gain.exponentialRampToValueAtTime(0.28 * i, now + 0.005);
    clankGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    clank.connect(clankFilter).connect(clankGain).connect(this.sfxGain);
    clank.start(now, Math.random() * 1.5, 0.22);

    // Debris/shimmer — high-frequency tail so the impact reads as not over
    // the instant the clank hits.
    const debris = this.ctx.createBufferSource();
    debris.buffer = this.noiseBuffer;
    debris.playbackRate.value = 2.1;
    const debrisFilter = this.ctx.createBiquadFilter();
    debrisFilter.type = 'highpass';
    debrisFilter.frequency.value = 3200;
    const debrisGain = this.ctx.createGain();
    debrisGain.gain.setValueAtTime(0.0001, now + 0.01);
    debrisGain.gain.exponentialRampToValueAtTime(0.085 * i, now + 0.025);
    debrisGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
    debris.connect(debrisFilter).connect(debrisGain).connect(this.sfxGain);
    debris.start(now + 0.005, Math.random() * 1.2, 0.32);
  }

  playExplosion(size: number): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer) return;
    const now = this.ctx.currentTime;
    const clampedSize = Math.max(1, Math.min(size, 12));
    const duration = 0.55 + clampedSize * 0.06;

    const noise = this.ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    noise.playbackRate.value = 0.7 + Math.random() * 0.4;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1400, now);
    filter.frequency.exponentialRampToValueAtTime(180, now + duration);

    const noiseGain = this.ctx.createGain();
    const peak = 0.22 + clampedSize * 0.025;
    noiseGain.gain.setValueAtTime(0.0001, now);
    noiseGain.gain.exponentialRampToValueAtTime(peak, now + 0.015);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    noise.connect(filter).connect(noiseGain).connect(this.sfxGain);
    noise.start(now);
    noise.stop(now + duration + 0.1);

    const sub = this.ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(150, now);
    sub.frequency.exponentialRampToValueAtTime(38, now + duration * 0.8);
    const subGain = this.ctx.createGain();
    const subPeak = 0.24 + clampedSize * 0.02;
    subGain.gain.setValueAtTime(0.0001, now);
    subGain.gain.exponentialRampToValueAtTime(subPeak, now + 0.04);
    subGain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    sub.connect(subGain).connect(this.sfxGain);
    sub.start(now);
    sub.stop(now + duration + 0.05);

    const crackle = this.ctx.createBufferSource();
    crackle.buffer = this.noiseBuffer;
    crackle.playbackRate.value = 2;
    const crackleFilter = this.ctx.createBiquadFilter();
    crackleFilter.type = 'highpass';
    crackleFilter.frequency.value = 2500;
    const crackleGain = this.ctx.createGain();
    crackleGain.gain.setValueAtTime(0.0001, now);
    crackleGain.gain.exponentialRampToValueAtTime(0.08, now + 0.008);
    crackleGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
    crackle
      .connect(crackleFilter)
      .connect(crackleGain)
      .connect(this.sfxGain);
    crackle.start(now, Math.random() * 1.5, 0.18);
  }

  playShipTouchdown(): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer) return;
    const now = this.ctx.currentTime;

    const click = this.ctx.createBufferSource();
    click.buffer = this.noiseBuffer;
    click.playbackRate.value = 2.5;
    const clickFilter = this.ctx.createBiquadFilter();
    clickFilter.type = 'highpass';
    clickFilter.frequency.value = 4200;
    const clickGain = this.ctx.createGain();
    clickGain.gain.setValueAtTime(0.0001, now);
    clickGain.gain.exponentialRampToValueAtTime(0.16, now + 0.003);
    clickGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.045);
    click.connect(clickFilter).connect(clickGain).connect(this.sfxGain);
    click.start(now, Math.random() * 1.5, 0.05);

    const hiss = this.ctx.createBufferSource();
    hiss.buffer = this.noiseBuffer;
    hiss.playbackRate.value = 1.4;
    const hissFilter = this.ctx.createBiquadFilter();
    hissFilter.type = 'bandpass';
    hissFilter.frequency.setValueAtTime(3400, now);
    hissFilter.frequency.exponentialRampToValueAtTime(1600, now + 0.5);
    hissFilter.Q.value = 1.4;
    const hissGain = this.ctx.createGain();
    hissGain.gain.setValueAtTime(0.0001, now + 0.02);
    hissGain.gain.exponentialRampToValueAtTime(0.16, now + 0.05);
    hissGain.gain.setValueAtTime(0.16, now + 0.32);
    hissGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
    hiss.connect(hissFilter).connect(hissGain).connect(this.sfxGain);
    hiss.start(now + 0.015, Math.random() * 1.2, 0.58);

    const thud = this.ctx.createOscillator();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(135, now + 0.36);
    thud.frequency.exponentialRampToValueAtTime(52, now + 0.56);
    const thudGain = this.ctx.createGain();
    thudGain.gain.setValueAtTime(0.0001, now + 0.35);
    thudGain.gain.exponentialRampToValueAtTime(0.22, now + 0.37);
    thudGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
    thud.connect(thudGain).connect(this.sfxGain);
    thud.start(now + 0.34);
    thud.stop(now + 0.62);
  }

  playShipTakeoff(): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer) return;
    const now = this.ctx.currentTime;
    const duration = 1.35;

    const ignition = this.ctx.createBufferSource();
    ignition.buffer = this.noiseBuffer;
    ignition.playbackRate.value = 1.6;
    const ignFilter = this.ctx.createBiquadFilter();
    ignFilter.type = 'bandpass';
    ignFilter.frequency.value = 2200;
    ignFilter.Q.value = 1.4;
    const ignGain = this.ctx.createGain();
    ignGain.gain.setValueAtTime(0.0001, now);
    ignGain.gain.exponentialRampToValueAtTime(0.22, now + 0.008);
    ignGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
    ignition.connect(ignFilter).connect(ignGain).connect(this.sfxGain);
    ignition.start(now, Math.random() * 1.2, 0.18);

    const sub = this.ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(38, now);
    sub.frequency.linearRampToValueAtTime(32, now + duration);
    const subGain = this.ctx.createGain();
    subGain.gain.setValueAtTime(0.0001, now);
    subGain.gain.exponentialRampToValueAtTime(0.28, now + 0.55);
    subGain.gain.setValueAtTime(0.28, now + duration - 0.3);
    subGain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    sub.connect(subGain).connect(this.sfxGain);
    sub.start(now);
    sub.stop(now + duration + 0.05);

    const roar = this.ctx.createBufferSource();
    roar.buffer = this.noiseBuffer;
    roar.loop = true;
    roar.playbackRate.value = 0.5;
    const roarFilter = this.ctx.createBiquadFilter();
    roarFilter.type = 'lowpass';
    roarFilter.frequency.setValueAtTime(220, now);
    roarFilter.frequency.exponentialRampToValueAtTime(1400, now + 0.7);
    roarFilter.frequency.exponentialRampToValueAtTime(900, now + duration);
    const roarGain = this.ctx.createGain();
    roarGain.gain.setValueAtTime(0.0001, now + 0.02);
    roarGain.gain.exponentialRampToValueAtTime(0.32, now + 0.55);
    roarGain.gain.setValueAtTime(0.32, now + duration - 0.3);
    roarGain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    roar.connect(roarFilter).connect(roarGain).connect(this.sfxGain);
    roar.start(now);
    roar.stop(now + duration + 0.08);
  }

  playWhoosh(): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer) return;
    const now = this.ctx.currentTime;
    const duration = 0.9;
    const noise = this.ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    noise.playbackRate.value = 0.8;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(350, now);
    filter.frequency.exponentialRampToValueAtTime(1600, now + duration * 0.6);
    filter.frequency.exponentialRampToValueAtTime(400, now + duration);
    filter.Q.value = 1.2;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(0.28, now + duration * 0.3);
    gain.gain.linearRampToValueAtTime(0.001, now + duration);

    noise.connect(filter).connect(gain).connect(this.sfxGain);
    noise.start(now);
    noise.stop(now + duration + 0.05);
  }

  playConfirm(): void {
    if (!this.ctx || !this.sfxGain) return;
    const now = this.ctx.currentTime;
    this.playTone(660, 0.12, now, 0.12);
    this.playTone(990, 0.14, now + 0.06, 0.12);
  }

  playPrompt(): void {
    if (!this.ctx || !this.sfxGain) return;
    this.playTone(840, 0.08, this.ctx.currentTime, 0.06);
  }

  startSpaceAmbient(): void {
    if (!this.ctx || !this.ambientGain) {
      this.pendingAmbient = 'space';
      return;
    }
    this.pendingAmbient = 'space';
    if (this.space) return;
    this.stopPlanetAmbient();

    const now = this.ctx.currentTime;
    const oscA = this.ctx.createOscillator();
    oscA.type = 'sine';
    oscA.frequency.value = 58;

    const oscB = this.ctx.createOscillator();
    oscB.type = 'sine';
    oscB.frequency.value = 87;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 500;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(0.08, now + 1.5);

    const lfo = this.ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.09;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 0.035;
    lfo.connect(lfoGain).connect(gain.gain);

    oscA.connect(filter);
    oscB.connect(filter);
    filter.connect(gain).connect(this.ambientGain);

    oscA.start(now);
    oscB.start(now);
    lfo.start(now);

    this.space = { oscA, oscB, gain, lfo, lfoGain };
  }

  stopSpaceAmbient(): void {
    if (this.pendingAmbient === 'space') this.pendingAmbient = null;
    if (!this.ctx || !this.space) return;
    const now = this.ctx.currentTime;
    const nodes = this.space;
    this.space = null;
    nodes.gain.gain.cancelScheduledValues(now);
    nodes.gain.gain.setValueAtTime(nodes.gain.gain.value, now);
    nodes.gain.gain.linearRampToValueAtTime(0, now + 0.8);
    setTimeout(() => {
      try {
        nodes.oscA.stop();
      } catch {
        /* noop */
      }
      try {
        nodes.oscB.stop();
      } catch {
        /* noop */
      }
      try {
        nodes.lfo.stop();
      } catch {
        /* noop */
      }
    }, 900);
  }

  startPlanetAmbient(): void {
    if (!this.ctx || !this.ambientGain || !this.noiseBuffer) {
      this.pendingAmbient = 'planet';
      return;
    }
    this.pendingAmbient = 'planet';
    if (this.planet) return;
    this.stopSpaceAmbient();

    const now = this.ctx.currentTime;
    const source = this.ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.loop = true;
    source.playbackRate.value = 0.55;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 280;
    filter.Q.value = 0.5;

    const highpass = this.ctx.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = 60;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(0.16, now + 1.5);

    const filterLfo = this.ctx.createOscillator();
    filterLfo.type = 'sine';
    filterLfo.frequency.value = 0.11;
    const filterLfoGain = this.ctx.createGain();
    filterLfoGain.gain.value = 120;
    filterLfo.connect(filterLfoGain).connect(filter.frequency);

    source.connect(filter).connect(highpass).connect(gain).connect(this.ambientGain);
    source.start(now);
    filterLfo.start(now);

    this.planet = { source, gain, filter, filterLfo, filterLfoGain };
  }

  stopPlanetAmbient(): void {
    if (this.pendingAmbient === 'planet') this.pendingAmbient = null;
    if (!this.ctx || !this.planet) return;
    const now = this.ctx.currentTime;
    const nodes = this.planet;
    this.planet = null;
    nodes.gain.gain.cancelScheduledValues(now);
    nodes.gain.gain.setValueAtTime(nodes.gain.gain.value, now);
    nodes.gain.gain.linearRampToValueAtTime(0, now + 0.7);
    setTimeout(() => {
      try {
        nodes.source.stop();
      } catch {
        /* noop */
      }
      try {
        nodes.filterLfo.stop();
      } catch {
        /* noop */
      }
    }, 800);
  }

  startEngine(): void {
    if (!this.ctx || !this.engineBus || !this.noiseBuffer) {
      this.pendingEngine = true;
      return;
    }
    this.pendingEngine = true;
    if (this.engine) return;

    const now = this.ctx.currentTime;

    const noise = this.ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    noise.loop = true;
    noise.playbackRate.value = 0.5;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 380;
    filter.Q.value = 0.6;
    const gain = this.ctx.createGain();
    gain.gain.value = 0.0001;
    noise.connect(filter).connect(gain).connect(this.engineBus);
    noise.start(now);

    const toneA = this.ctx.createOscillator();
    toneA.type = 'sine';
    toneA.frequency.value = 76;
    const toneB = this.ctx.createOscillator();
    toneB.type = 'sine';
    toneB.frequency.value = 114;
    const toneFilter = this.ctx.createBiquadFilter();
    toneFilter.type = 'lowpass';
    toneFilter.frequency.value = 420;
    toneFilter.Q.value = 0.7;
    const toneGain = this.ctx.createGain();
    toneGain.gain.value = 0.0001;
    toneA.connect(toneFilter);
    toneB.connect(toneFilter);
    toneFilter.connect(toneGain).connect(this.engineBus);
    toneA.start(now);
    toneB.start(now);

    this.engine = {
      noise,
      filter,
      gain,
      toneA,
      toneB,
      toneFilter,
      toneGain
    };
    this.applyEngineLevel();
  }

  stopEngine(): void {
    this.pendingEngine = false;
    if (!this.ctx || !this.engine) return;
    const now = this.ctx.currentTime;
    const nodes = this.engine;
    this.engine = null;
    nodes.gain.gain.cancelScheduledValues(now);
    nodes.gain.gain.setValueAtTime(nodes.gain.gain.value, now);
    nodes.gain.gain.linearRampToValueAtTime(0.0001, now + 0.4);
    nodes.toneGain.gain.cancelScheduledValues(now);
    nodes.toneGain.gain.setValueAtTime(nodes.toneGain.gain.value, now);
    nodes.toneGain.gain.linearRampToValueAtTime(0.0001, now + 0.4);
    setTimeout(() => {
      try {
        nodes.noise.stop();
      } catch {
        /* noop */
      }
      try {
        nodes.toneA.stop();
      } catch {
        /* noop */
      }
      try {
        nodes.toneB.stop();
      } catch {
        /* noop */
      }
    }, 450);
  }

  setEngineLevel(intensity: number): void {
    this.engineLevel = Math.max(0, Math.min(1, intensity));
    this.applyEngineLevel();
  }

  private applyEngineLevel(): void {
    if (!this.ctx || !this.engine) return;
    const now = this.ctx.currentTime;
    const level = this.engineLevel;

    const noiseTarget = Math.pow(level, 1.25) * 0.22;
    const noiseFreq = 320 + level * 1100;
    this.engine.gain.gain.cancelScheduledValues(now);
    this.engine.gain.gain.setValueAtTime(this.engine.gain.gain.value, now);
    this.engine.gain.gain.linearRampToValueAtTime(noiseTarget, now + 0.22);
    this.engine.filter.frequency.setTargetAtTime(noiseFreq, now, 0.28);

    const toneTarget = Math.pow(level, 1.8) * 0.055;
    const baseFreqA = 72 + level * 22;
    const baseFreqB = 108 + level * 32;
    const toneCutoff = 360 + level * 360;
    this.engine.toneGain.gain.cancelScheduledValues(now);
    this.engine.toneGain.gain.setValueAtTime(
      this.engine.toneGain.gain.value,
      now
    );
    this.engine.toneGain.gain.linearRampToValueAtTime(toneTarget, now + 0.3);
    this.engine.toneA.frequency.setTargetAtTime(baseFreqA, now, 0.35);
    this.engine.toneB.frequency.setTargetAtTime(baseFreqB, now, 0.35);
    this.engine.toneFilter.frequency.setTargetAtTime(toneCutoff, now, 0.35);
  }

  /** Sharp "breaking the sound barrier" hit — used at jump start AND arrival.
   *  Layered: a wide bass drop, a high noise burst that sweeps down, and a
   *  short sub-thump at the bottom. Punchier than playWhoosh. */
  playSonicBoom(): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer) return;
    const now = this.ctx.currentTime;

    // Sub drop — chest-felt impact.
    const sub = this.ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(180, now);
    sub.frequency.exponentialRampToValueAtTime(38, now + 0.55);
    const subGain = this.ctx.createGain();
    subGain.gain.setValueAtTime(0.0001, now);
    subGain.gain.exponentialRampToValueAtTime(0.42, now + 0.025);
    subGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
    sub.connect(subGain).connect(this.sfxGain);
    sub.start(now);
    sub.stop(now + 0.75);

    // White noise crack with a dramatic downward filter sweep.
    const noise = this.ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    noise.playbackRate.value = 1.1 + Math.random() * 0.25;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(6800, now);
    filter.frequency.exponentialRampToValueAtTime(220, now + 0.5);
    filter.Q.value = 1.6;
    const noiseGain = this.ctx.createGain();
    noiseGain.gain.setValueAtTime(0.0001, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.36, now + 0.018);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.65);
    noise.connect(filter).connect(noiseGain).connect(this.sfxGain);
    noise.start(now, Math.random() * 1.5, 0.7);

    // Metallic ping at the front so the impact has an edge.
    const ping = this.ctx.createOscillator();
    ping.type = 'triangle';
    ping.frequency.setValueAtTime(520, now);
    ping.frequency.exponentialRampToValueAtTime(280, now + 0.18);
    const pingGain = this.ctx.createGain();
    pingGain.gain.setValueAtTime(0.0001, now);
    pingGain.gain.exponentialRampToValueAtTime(0.12, now + 0.005);
    pingGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    ping.connect(pingGain).connect(this.sfxGain);
    ping.start(now);
    ping.stop(now + 0.24);
  }

  /** Looped imposing roar that overlays the regular engine sound while the
   *  ship is in a hyperspace jump. Heavier sub, harmonics, and a slow
   *  modulator — distinct from the steady bandpass-filtered engine hum. */
  startJumpDrive(): void {
    if (!this.ctx || !this.sfxGain || !this.noiseBuffer || this.jumpDrive) return;
    const now = this.ctx.currentTime;

    const noise = this.ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    noise.loop = true;
    noise.playbackRate.value = 0.42;
    const noiseFilter = this.ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.value = 220;
    noiseFilter.Q.value = 0.45;
    const noiseGain = this.ctx.createGain();
    noiseGain.gain.setValueAtTime(0.0001, now);
    noiseGain.gain.linearRampToValueAtTime(0.34, now + 0.4);
    noise.connect(noiseFilter).connect(noiseGain).connect(this.sfxGain);

    const sub = this.ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = 42;
    const subGain = this.ctx.createGain();
    subGain.gain.setValueAtTime(0.0001, now);
    subGain.gain.linearRampToValueAtTime(0.32, now + 0.3);
    sub.connect(subGain).connect(this.sfxGain);

    const hum = this.ctx.createOscillator();
    hum.type = 'sawtooth';
    hum.frequency.value = 96;
    const humFilter = this.ctx.createBiquadFilter();
    humFilter.type = 'lowpass';
    humFilter.frequency.value = 540;
    humFilter.Q.value = 1.1;
    const humGain = this.ctx.createGain();
    humGain.gain.setValueAtTime(0.0001, now);
    humGain.gain.linearRampToValueAtTime(0.085, now + 0.4);
    hum.connect(humFilter).connect(humGain).connect(this.sfxGain);

    // Slow tremolo on the hum so the drive feels alive, not flat.
    const modLfo = this.ctx.createOscillator();
    modLfo.type = 'sine';
    modLfo.frequency.value = 4.2;
    const modLfoGain = this.ctx.createGain();
    modLfoGain.gain.value = 0.025;
    modLfo.connect(modLfoGain).connect(humGain.gain);

    noise.start(now);
    sub.start(now);
    hum.start(now);
    modLfo.start(now);

    this.jumpDrive = {
      noise,
      noiseFilter,
      noiseGain,
      sub,
      subGain,
      hum,
      humFilter,
      humGain,
      modLfo,
      modLfoGain
    };
  }

  stopJumpDrive(): void {
    if (!this.ctx || !this.jumpDrive) return;
    const now = this.ctx.currentTime;
    const nodes = this.jumpDrive;
    this.jumpDrive = null;

    const fade = 0.35;
    for (const g of [nodes.noiseGain, nodes.subGain, nodes.humGain]) {
      g.gain.cancelScheduledValues(now);
      g.gain.setValueAtTime(g.gain.value, now);
      g.gain.linearRampToValueAtTime(0.0001, now + fade);
    }
    setTimeout(() => {
      try { nodes.noise.stop(); } catch { /* noop */ }
      try { nodes.sub.stop(); } catch { /* noop */ }
      try { nodes.hum.stop(); } catch { /* noop */ }
      try { nodes.modLfo.stop(); } catch { /* noop */ }
    }, fade * 1000 + 50);
  }

  private playTone(
    freq: number,
    peak: number,
    startTime: number,
    duration: number
  ): void {
    if (!this.ctx || !this.sfxGain) return;
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.exponentialRampToValueAtTime(peak, startTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
    osc.connect(gain).connect(this.sfxGain);
    osc.start(startTime);
    osc.stop(startTime + duration + 0.02);
  }

  private createNoiseBuffer(durationSec: number): AudioBuffer | null {
    if (!this.ctx) return null;
    const sampleRate = this.ctx.sampleRate;
    const frames = Math.floor(durationSec * sampleRate);
    const buffer = this.ctx.createBuffer(1, frames, sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < frames; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    return buffer;
  }
}
